import { eventBus } from '../event-manager/event-bus';
import { leaveSession } from '../handlers';
import { setReceiverConstraints } from '../handlers/constraints';
import JitsiMeetJS from '../lib-jitsi-meet';
import type { ConnectionStats } from '../lib-jitsi-meet/type';
import {
  lowerHandLocal,
  startRecording,
  useConferenceStore,
  useConfigStore,
  useConnectionStore,
  useParticipantStore,
} from '../store';
import { getLocalTrack, useTracksStore } from '../store/tracks';
import {
  EVENT_LISTENER_METHODS,
  INTERNAL_EVENTS,
  MEDIA_TYPE,
  PARTICIPANT_ROLE,
} from '../utils';
import { sendParticipantEvent } from '../utils/send-participant-event';
import { onTrack } from './onTrack';

const AUDIO_LEVEL_THRESHOLD_TO_LOWER_HAND = 0.2;

class ConferenceListener {
  public onConferenceJoined() {
    useConferenceStore.setState({
      conferenceJoined: true,
      conferenceStatus: 'joined',
      conferenceJoinedAt: Date.now(),
    });
    eventBus.publish({
      type: EVENT_LISTENER_METHODS.SessionStatusListener.onSessionJoined,
    });
    eventBus.publish({
      type: INTERNAL_EVENTS.onSessionJoined,
    });

    const autoStartRecording = useConfigStore.getState().autoStartRecording;
    if (autoStartRecording) {
      setTimeout(() => {
        console.log('Starting recording');
        startRecording();
      }, 500);
    }

    // setTimeout(() => {
    //   const conference = useConferenceStore.getState().conference;
    //   if (conference) {
    //     console.log('Starting transcription');
    //     conference.dial('jitsi_meet_transcribe');
    //   }
    // }, 5000);
  }

  public async onConferenceLeft() {
    useConferenceStore.setState({
      conferenceJoined: false,
      conferenceStatus: 'left',
    });
    console.log('Conference left');
    eventBus.publish({
      type: EVENT_LISTENER_METHODS.SessionStatusListener.onSessionLeft,
    });
    await useConnectionStore.getState().disconnect();
  }

  public onTrackAdded(track: any) {
    if (track.isLocal()) {
      return;
    }
    onTrack(track);
  }

  public onUserJoined(_id: string, participant: any) {
    const id: string = participant.getId();
    const displayName = participant.getDisplayName();
    const role = participant.getRole();

    if (participant.isHidden()) {
      return;
    }

    eventBus.publish({
      type: INTERNAL_EVENTS.notification,
      payload: {
        type: 'info',
        message: `${displayName} has joined the conference`,
      },
    });

    useParticipantStore.getState().addParticipant({
      name: displayName ?? 'Unknown',
      pid: id,
      ruid: `${id}-human`,
      role: role,
      isLocal: false,
      sources: participant.getSources(),
      type: 'human',
    });
  }

  public onUserLeft(_id: string, participant: any) {
    if (participant.isHidden()) {
      return;
    }

    const id: string = participant.getId();

    eventBus.publish({
      type: INTERNAL_EVENTS.notification,
      payload: {
        type: 'info',
        message: `${participant.getDisplayName()} has left the conference`,
      },
    });

    useParticipantStore.getState().removeParticipant(id);
  }

  public onTrackRemoved(track: any) {
    if (track.isLocal()) {
      // This is because for local tracks, we don't want the other information to go away such as muted.
      // So in case the track is recreated, it is still muted/unmuted based on the previous state.
      const localTrack = getLocalTrack(track.getType());
      if (localTrack?.originalTrack === track) {
        useTracksStore.getState().updateLocalTrack(track.getType(), {
          originalTrack: undefined,
        });
      }
    } else {
      useTracksStore.getState().removeTrack(track);
    }
    track.removeAllListeners(JitsiMeetJS.events.track.TRACK_MUTE_CHANGED);
    track.removeAllListeners(JitsiMeetJS.events.track.TRACK_VIDEOTYPE_CHANGED);
    track.removeAllListeners(JitsiMeetJS.events.track.NO_DATA_FROM_SOURCE);
  }

  public onConferenceJoinInProgress() {
    // This event is triggered when the conference is in the process of joining.
  }

  public onConferenceFailed(errorName: any, error: string, message: string) {
    console.log();

    if (errorName === JitsiMeetJS.errors.conference.CONFERENCE_DESTROYED) {
      leaveSession({ forceLeave: true });
      return;
    }

    console.error('Conference failed:', errorName, error, message);

    useConferenceStore.setState({
      conferenceStatus: 'error',
      conferenceJoined: false,
      conferenceError: message?.toString() || 'Unknown error',
    });
  }

  public onRecorderStateChanged(session: any) {
    console.log(
      'Recorder state changed onRecorderStateChanged:',
      session,
      session.getStatus(),
    );
    if (session) {
      const sessionId = session.getID();
      if (sessionId) {
        const status = session.getStatus();
        const mode = session.getMode();
        const error = session.getError();
        const initiator = session.getInitiator();
        useConferenceStore.setState({
          recording: {
            status,
            mode,
            error,
            sessionId,
            initiator,
          },
        });
        const conference = useConferenceStore.getState().conference;
        const localParticipant =
          useParticipantStore.getState().localParticipant;

        // For v4 compatibility
        conference.sendCommandOnce('START_SCREEN_RECORDING', {
          attributes: {
            recordId: sessionId,
            ...localParticipant,
            id: localParticipant.pid,
          },
        });
      }
    }
  }

  public onConnectionEstablished() {
    useConferenceStore.setState({
      conferenceStatus: 'joined',
    });
  }

  public onConnectionInterrupted() {
    useConferenceStore.setState({
      conferenceStatus: 'interrupted',
    });
  }

  public onConnectionRestored() {
    useConferenceStore.setState({
      conferenceStatus: 'restored',
    });
  }

  public onP2PStatus(_conference: any, p2p: boolean) {
    useConferenceStore.setState({
      p2p,
    });
  }

  public onTrackMuteChanged(track: any, participantThatMutedUs: any) {
    if (participantThatMutedUs) {
      useTracksStore.getState().updateTrack(track, {
        muted: track.isMuted() ? 1 : 0,
      });
      const displayName = participantThatMutedUs.getDisplayName();
      if (displayName) {
        eventBus.publish({
          type: INTERNAL_EVENTS.notification,
          payload: {
            type: 'info',
            message: `${displayName} has muted you.`,
          },
        });
      }
    }
  }

  public onParticipantSourceUpdated(participant: any) {
    console.log('SOURCE_UPDATED', participant);
    setReceiverConstraints();
  }

  public onConferenceCreatedTimestamp(timestamp: number) {
    useConferenceStore.setState({
      conferenceStartedAt: timestamp,
    });
  }

  onDominantSpeakerChanged(
    dominant: string,
    previous: string[] | undefined,
    _silent: boolean | string,
  ) {
    useParticipantStore.setState({
      dominantSpeakers: [dominant, ...(previous ?? [])],
    });
    sendParticipantEvent(
      EVENT_LISTENER_METHODS.ParticipantEventsListner.onDominantSpeakerChanged,
      dominant,
    );

    const localParticipant = useParticipantStore.getState().localParticipant;
    if (dominant === localParticipant.pid) {
      lowerHandLocal();
    }
  }

  onLocalStatsUpdated(stats: ConnectionStats) {
    console.log('LOCAL_STATS_UPDATED', stats);
    const localParticipant = useParticipantStore.getState().localParticipant;
    if (stats.connectionQuality) {
      useConferenceStore.setState(state => {
        const connectionQuality = new Map(state.connectionQuality);
        connectionQuality.set(localParticipant.pid, stats.connectionQuality);
        return { connectionQuality };
      });
    }
  }

  onRemoteStatsUpdated(id: string, stats: ConnectionStats) {
    console.log('REMOTE_STATS_UPDATED', id, stats);
    if (stats.connectionQuality) {
      useConferenceStore.setState(state => {
        const connectionQuality = new Map(state.connectionQuality);
        connectionQuality.set(id, stats.connectionQuality);
        return { connectionQuality };
      });
    }
  }

  onParticipantPropertyChanged(
    participant: any,
    propertyName: string,
    _oldValue: string,
    newValue: string,
  ) {
    const participantId = participant.getId();
    if (participantId && propertyName === 'raisedHand') {
      if (newValue) {
        useConferenceStore
          .getState()
          .raiseHand(participantId, parseInt(newValue, 10));
      } else {
        useConferenceStore.getState().lowerHand(participantId);
      }
    }
  }

  onUserRoleChanged(participantId: any, newRole: string) {
    if (
      typeof participantId === 'string' &&
      (newRole === PARTICIPANT_ROLE.MODERATOR ||
        newRole === PARTICIPANT_ROLE.NONE)
    ) {
      useParticipantStore.getState().updateParticipant(participantId, {
        role: newRole,
      });
    }
  }

  onTalkWhileMuted() {}

  onConferenceError(error: any) {
    console.error('Conference error:', error);
    if (error instanceof Error) {
      useConferenceStore.setState({
        conferenceStatus: 'error',
        conferenceJoined: false,
        conferenceError: error.message,
      });
    } else {
      useConferenceStore.setState({
        conferenceStatus: 'error',
        conferenceJoined: false,
        conferenceError: error.toString(),
      });
    }
  }

  onTrackAudioLevelChanged(participantId: string, audioLevel: number) {
    const localParticipant = useParticipantStore.getState().localParticipant;
    if (participantId !== localParticipant.pid) {
      return;
    }

    const localAudioTrack = getLocalTrack(MEDIA_TYPE.AUDIO);
    if (localAudioTrack?.muted) {
      return;
    }

    const raiseHandMap = useConferenceStore.getState().raiseHandMap;
    const hasRaisedHand = raiseHandMap.has(localParticipant.pid);

    if (hasRaisedHand && audioLevel > AUDIO_LEVEL_THRESHOLD_TO_LOWER_HAND) {
      lowerHandLocal();
    }
  }
}

export const conferenceListener = new ConferenceListener();
